import { createContext, useContext, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { translations, languages } from './translations';
import { langFromPath, stripLang, localizePath } from './langPath';

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  // El idioma lo manda la dirección: la portada y las rutas sin prefijo son
  // español, y cada idioma tiene su propio prefijo (/ca, /en, /fr). Así cada
  // versión tiene una dirección propia e indexable, sin contenido duplicado.
  const lang = langFromPath(location.pathname);

  useEffect(() => {
    const locale = languages.find((l) => l.code === lang)?.locale || 'es';
    document.documentElement.lang = locale;
  }, [lang]);

  const setLang = useCallback((code) => {
    if (!translations[code]) return;
    const target = localizePath(
      stripLang(location.pathname) + location.search + location.hash,
      code,
    );
    const current = location.pathname + location.search + location.hash;
    if (target !== current) navigate(target);
  }, [location, navigate]);

  const t = useCallback((key) => {
    return translations[lang]?.[key] ?? translations.ES[key] ?? key;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    // Fallback if used outside provider
    return { lang: 'ES', setLang: () => {}, t: (k) => translations.ES[k] ?? k };
  }
  return ctx;
}